/**
 * Protected Route Component
 * 
 * Ensures routes require authentication before rendering.
 * Shows login modal for unauthenticated users while preserving page context.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { openLoginModal } = useAuthModal();
  const location = useLocation();

  // Show login modal if not authenticated (once loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      openLoginModal(location.pathname);
    }
  }, [isLoading, isAuthenticated, openLoginModal, location.pathname]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="oro-loading h-8 w-8 rounded-full bg-oro-gold mx-auto mb-4"></div>
          <p className="text-text-secondary">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show protected content only when authenticated
  if (!isAuthenticated) {
    // Return minimal placeholder while modal is triggered
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="oro-loading h-8 w-8 rounded-full bg-oro-gold mx-auto mb-4"></div>
          <p className="text-text-secondary">Authentication required...</p>
        </div>
      </div>
    );
  }

  // Render protected content
  return <>{children}</>;
}